import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import DownloadRoundedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewOutlined";

// A PDF needs no library: the browser has a reader, and this shows the file in
// it. Nothing here parses the document - it is handed to the browser, which
// draws it in a frame of its own, so a malformed or hostile PDF is the
// browser's problem rather than this page's.
//
// A PDF stored inline arrives as base64 and becomes a blob of this page's
// own, which the frame reads; one stored as a link opens that link, and only
// https ones ever reach here (the server drops the rest).

// Bytes from what is stored: base64, or a data: URL an import wrote.
const bytesOf = (content) => {
  const raw = String(content || "").replace(/^data:[^,]+,/, "");
  const binary = atob(raw);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const PdfCanvas = forwardRef(function PdfCanvas({ url, content, name, onDownload }, ref) {
  const [href, setHref] = useState(url || null);
  const [error, setError] = useState("");
  const title = useMemo(() => `${name || "Diagram"} (PDF)`, [name]);

  // The reader's own zoom is the PDF viewer's; the toolbar's zoom buttons are
  // hidden for this format rather than doing nothing.
  useImperativeHandle(ref, () => ({}));

  useEffect(() => {
    if (url) {
      setHref(url);
      setError("");
      return undefined;
    }
    if (!content) {
      setHref(null);
      setError("This PDF has no content.");
      return undefined;
    }
    let objectUrl = null;
    try {
      objectUrl = URL.createObjectURL(new Blob([bytesOf(content)], { type: "application/pdf" }));
      setHref(objectUrl);
      setError("");
    } catch (err) {
      setHref(null);
      setError("This PDF couldn't be read. It may have been stored incompletely.");
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, content]);

  if (error) {
    return (
      <div className="bf-pdf-error" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="bf-pdf-canvas">
      <iframe className="bf-pdf-canvas__frame" title={title} src={href || ""} />
      <div className="bf-pdf-canvas__actions">
        <Button size="small" startIcon={<OpenInNewRoundedIcon />} href={href || undefined} target="_blank" rel="noopener noreferrer">
          Open in a new tab
        </Button>
        {onDownload && (
          <Button size="small" startIcon={<DownloadRoundedIcon />} onClick={onDownload}>
            Download
          </Button>
        )}
      </div>
    </div>
  );
});

export default PdfCanvas;
